/** Shapes of plan-data.json, section 4. Content is static and never changes at runtime. */

export interface Exercise {
  n: string;            // name
  s: number;            // sets as prescribed (tick targets), NOT real working sets
  r: string;            // rep string; "each side"/"each leg"/"each arm" doubles real sets
  c: string;            // coaching note
  m?: string[];         // muscle tags
}

export interface LiftSession { name: string; focus: string; ex: Exercise[] }
export interface RecoverySession { name: string; focus: string; ex: Exercise[] }

export interface RunBlock { n: string; s: number; min: number; c?: string }
export interface RunSession { title: string; note: string; blocks: RunBlock[] }

export interface FoodItem {
  n: string; k: number; p: number; f: number; c: number; t: string; d: string;
}

export interface Criterion { t: string; auto?: number }
export interface Gate {
  lvl: number; week: number; name: string; opens: string; why: string; crit: Criterion[];
}

export type SlotName = 'breakfast' | 'am' | 'lunch' | 'pm' | 'dinner' | 'post';

export interface PlanData {
  meta: {
    title: string;
    subject: Record<string, unknown>;
    energy: Record<string, unknown>;
    scheduleByDow: Record<string, string>;
    deloadWeeks: number[];
    slots: SlotName[];
    postSlotDows: number[];
    mealRotationOffsets: Record<SlotName, number>;
    defaultStartDate: string;
  };
  trajectory: { weightKg: number[]; bodyFatPct: number[] };
  lifts: Record<string, Record<'A' | 'B', LiftSession>>;
  runs: Record<string, RunSession[]>;
  food: Record<SlotName, FoodItem[]>;
  recovery: { wed: RecoverySession; sun: RecoverySession };
  gates: Gate[];
  rules: [string, string][];
  runWarmUp: Exercise;
  runCoolDown: Exercise;
}

/** Read-only snapshot of stored state, so plan functions stay pure and synchronous.
 *  Built once per render from the SQLite store. */
export interface PlanState {
  get<T = unknown>(key: string): T | undefined;
  has(key: string): boolean;
  /** Live entries under a prefix, tombstones already excluded. */
  entries(prefix: string): Array<[string, unknown]>;
}

export function stateFromMap(map: Map<string, unknown>): PlanState {
  return {
    get: <T,>(k: string) => map.get(k) as T | undefined,
    has: (k) => map.has(k),
    entries: (prefix) => [...map.entries()].filter(([k]) => k.startsWith(prefix)),
  };
}
